/**
 * recommendationExecutionService.ts
 * P4B Phase 3: Client-side service for logging recommendation executions.
 *
 * Sends execution records to POST /lender/recommendation-executions.
 * Fire-and-forget — failures are logged (DEV only) but never block the UI.
 *
 * Privacy: No member PII sent. Only recommendation-level operational fields.
 * Attribution: Observational language only — no causal framing.
 */

// Match lenderApiService.ts / recommendationEventService.ts pattern
const API_BASE = import.meta.env.VITE_API_URL || '/api';
const VS_CLIENT_ID = import.meta.env.VITE_VS_CLIENT_ID;

/**
 * Controlled vocabulary for execution types.
 * Must match the CHECK constraint in recommendation_executions table.
 */
export type ExecutionType =
  | 'outreach_email'
  | 'outreach_call'
  | 'financial_counseling'
  | 'loan_modification'
  | 'fee_adjustment'
  | 'product_offer'
  | 'internal_review'
  | 'policy_change'
  | 'referral'
  | 'other';

/** Human-readable labels for each execution type (UI dropdown). */
export const EXECUTION_TYPE_LABELS: Record<ExecutionType, string> = {
  outreach_email: 'Sent outreach email',
  outreach_call: 'Made outreach call',
  financial_counseling: 'Provided financial counseling',
  loan_modification: 'Initiated loan modification',
  fee_adjustment: 'Adjusted fees',
  product_offer: 'Extended product offer',
  internal_review: 'Conducted internal review',
  policy_change: 'Updated policy',
  referral: 'Made referral',
  other: 'Other action',
};

/** All valid execution types as an ordered array (for dropdown rendering). */
export const EXECUTION_TYPES: ExecutionType[] = Object.keys(EXECUTION_TYPE_LABELS) as ExecutionType[];

export interface RecommendationExecutionPayload {
  recommendation_key: string;
  segment_key: string;
  execution_type: ExecutionType;
  execution_notes?: string;
  execution_metadata?: Record<string, unknown>;
}

/**
 * PII patterns for client-side pre-validation.
 * Matches the server-side patterns in lenderRecommendationExecutionHandler.
 */
const PII_PATTERNS: Array<{ name: string; regex: RegExp }> = [
  { name: 'SSN', regex: /\b\d{3}-\d{2}-\d{4}\b/ },
  { name: 'email', regex: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/ },
  { name: 'phone', regex: /\b(?:\+?1[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}\b/ },
];

/**
 * Client-side PII check for notes text.
 * Returns the name of the first detected PII pattern, or null if clean.
 */
export function detectPII(text: string): string | null {
  for (const { name, regex } of PII_PATTERNS) {
    if (regex.test(text)) return name;
  }
  return null;
}

/** Max character length for execution_notes. */
export const NOTES_MAX_LENGTH = 1000;

/**
 * Post a recommendation execution to the API (fire-and-forget).
 * Never throws — failures are silently logged in DEV.
 *
 * @returns true if the API accepted the request, false otherwise.
 */
export async function trackRecommendationExecution(
  payload: RecommendationExecutionPayload,
): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE}/verascore/lender/recommendation-executions`, {
      method: 'POST',
      headers: {
        'VS-Client-Id': VS_CLIENT_ID,
        'Content-Type': 'application/json',
        'x-request-purpose': 'execution_tracking',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      if (import.meta.env.DEV) {
        console.warn(
          `[recommendation-executions] API returned ${response.status} for ${payload.execution_type}:${payload.recommendation_key}`,
        );
      }
      return false;
    }

    if (import.meta.env.DEV) {
      console.log(
        `[recommendation-executions] Logged ${payload.execution_type} for ${payload.recommendation_key}`,
      );
    }
    return true;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[recommendation-executions] Failed to send execution:', err);
    }
    // Fire-and-forget: never throw
    return false;
  }
}
